import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  useGlobalToggle,
  closeGlobalToggles,
  CLOSE_GLOBAL_TOGGLE_EVENT,
} from 'edcom-ui';
import PropTypes from 'prop-types';
import { SvgIcon } from 'edcom-ui';
import Autosuggest from 'react-autosuggest';
import Suggestion from './Suggestion';
import searchSuggestions from './suggestions.json';
import popularSearchSuggestions from './popularSearchSuggestions.json';

/**
 * Construct popular searches suggestion array
 * @returns {Array} popular searches suggestions
 */
const popularSearches = () => {
  const popularSearches = [];

  for (let popularSearchSuggestion of popularSearchSuggestions[0].entries) {
    popularSearches.push({
      ...popularSearchSuggestion,
      index: 0,
      highlightLength: 0,
    });
  }

  const browseAllSuggestion = {
    display: '',
    term: null,
    highlightLength: 0,
    suggestionType: 'browseAll',
  };

  return [
    {
      title: 'Popular Searches',
      entries: [...popularSearches, browseAllSuggestion],
    },
  ];
};

/**
 *
 * @param {string} searchMetaTitle The search filters applied
 * @returns {string} trimmed filters
 */
const getFiltersInSuggestion = searchMetaTitle => {
  if (searchMetaTitle === undefined) {
    return null;
  }
  const index = searchMetaTitle.indexOf('All Educational Resources');
  if (index === -1) {
    return searchMetaTitle;
  }

  return searchMetaTitle.slice(0, index);
};

/**
 * Get a list of suggestions from a search term.
 *
 * @param {string} term The search term on which to base suggestions.
 * @param {string} searchMetaTitle The search filters applied
 * @param {string} page Let the method know which page the autocomplete sits in
 * @param {string} query The search query
 * @param {number} limit The max number of suggestions to return
 * @returns {SearchSuggestion[] | popularSearches[]}
 */
const getSuggestions = (
  searchTerm,
  searchMetaTitle,
  page,
  query,
  limit = 6
) => {
  const inputValue = searchTerm.trim().toLowerCase();
  const inputLength = inputValue.length;

  // Render popular searches when no input
  if (inputLength === 0) {
    if (page === 'search') return [];
    return popularSearches();
  }

  const values = [];
  const searchWithinTerm = searchSuggestions[0].entries.filter(
    entry => searchTerm === entry.display.toLowerCase()
  );
  const searchWithinSuggestion = {
    display: inputValue + ' in ' + getFiltersInSuggestion(searchMetaTitle),
    term: searchWithinTerm.length === 0 ? inputValue : searchWithinTerm[0].term,
    highlightLength: inputValue.length,
    suggestionType: 'searchWithin',
    searchValue: searchTerm,
    index: 0,
    termMatchSuggestion: searchWithinTerm.length !== 0,
  };

  const searchAllSuggestion = {
    display: `Search all resources for "${inputValue}"`,
    term: inputValue,
    highlightLength: inputValue.length,
    suggestionType: 'searchAll',
  };

  for (let suggestion of searchSuggestions[0].entries) {
    const { display } = suggestion;
    const index = display.toLowerCase().indexOf(inputValue);
    const highlightLength = inputValue.length;

    if (index !== -1) {
      values.push({ ...suggestion, highlightLength, index });
    }
    if (values.length >= limit) {
      break;
    }
  }

  // Add "Search within" on the search page when at least one filter is applied (e.g. content type or grade)
  if (
    page === 'search' &&
    query === undefined &&
    searchMetaTitle.length > 0 &&
    // TODO: Find a way to not use magic string
    searchMetaTitle !== 'All Educational Resources' &&
    !searchMetaTitle.startsWith('Showing Results')
  ) {
    values.unshift(searchWithinSuggestion);
    values.push(searchAllSuggestion);
  } else if (values.length > 0) {
    // Add "Search All" when we have at least one match
    values.push(searchAllSuggestion);
  }

  return [{ title: 'autosuggest', entries: [...values] }];
};

/**
 * Get the display value of a suggestion.
 *
 * @param {SearchSuggestion} suggestion
 * @returns {string}
 */
const getSuggestionValue = suggestion => {
  const { suggestionType } = suggestion;
  if (suggestionType === 'searchAll') {
    return suggestion.term;
  }
  return suggestion.display;
};

const AutoSuggestWrapper = ({
  doSubmit,
  value,
  searchInputClass,
  placeholder,
  onChange,
  page,
  searchMetaTitle,
  query,
  setSearchWithin,
  setSearchAutoSuggest,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const wrapperRef = useRef(null);
  // Use global toggle so other components (e.g. SubNavDropdown) can close the suggestion programmatically
  const [isOpen, setIsOpen] = useGlobalToggle(wrapperRef);
  const router = useRouter();

  useEffect(() => {
    // Close the suggestion dropdown when route changes
    router.events.on('routeChangeStart', handleSuggestionsClearRequested);
    return () =>
      router.events.off('routeChangeStart', handleSuggestionsClearRequested);
  }, []);

  useEffect(() => {
    if (isOpen) return;
    // Close other menus (SubNavDropdown, etc.) that use the GlobalToggle hook
    // Prevents case where open SubNavDropdown was covering suggestion dropdown
    closeGlobalToggles();
    // Use timeout to wait for SubNavDropdown slide up closing animation
    const openTimeout = setTimeout(() => {
      setIsOpen(!!suggestions);
    }, 200);
    const clearOpenTimeout = () => clearTimeout(openTimeout);

    // Add event listener to cancel pending open timeout when closed by globalToggle
    document.addEventListener(CLOSE_GLOBAL_TOGGLE_EVENT, clearOpenTimeout);
    return () => {
      document.removeEventListener(CLOSE_GLOBAL_TOGGLE_EVENT, clearOpenTimeout);
      clearOpenTimeout();
    };
  }, [suggestions]);

  useEffect(() => {
    const requestTimeout = setTimeout(async () => {
      if (value && suggestions[0]) {
        // Tricky way to know whether if there is a suggestion matched or not
        if (
          suggestions[0].entries.length === 0 ||
          (suggestions[0].entries[0].suggestionType === 'searchWithin' &&
            suggestions[0].entries[1].suggestionType === 'searchAll')
        ) {
          try {
            const res = await fetch(
              `https://${window.location.hostname}/api/auto-complete/misspelled`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );
            await res.json();
          } catch (error) {
            return { message: 'Connection failed' };
          }
        }
      }
    }, 400);

    return () => clearTimeout(requestTimeout);
  }, [value]);

  /**
   * Handle the selection of a suggestion.
   *
   * @param {Event} event
   * @param suggestion
   */
  const handleSuggestionSelected = (event, { suggestion }) => {
    const { suggestionType } = suggestion;
    if (suggestionType === 'browseAll') {
      router.push('/resources/').then();
      return;
    }
    if (suggestionType === 'searchWithin') {
      setSearchWithin(true);
    } else if (suggestionType === undefined) {
      setSearchAutoSuggest(true);
    }
    doSubmit(suggestion);
  };

  /**
   * Handle the request to update suggestions as the result of a value change.
   *
   * @param {object} args
   * @param {string} args.value The search term
   * @param {string} args.reason The reason suggestion is fetched
   */
  const handleSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value, searchMetaTitle, page, query));
  };

  /**
   * Handle the request to clear search suggestions.
   */
  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  /**
   * Render a suggestion value in the list.
   *
   * @param {SearchSuggestion} suggestion
   * @returns {Suggestion | JSXElement}
   */
  const renderSuggestion = suggestion => {
    if (!isOpen) return null;

    const { display, highlightLength, index, suggestionType } = suggestion;

    const end = index + highlightLength;

    switch (suggestionType) {
      case 'searchWithin':
        return (
          <Suggestion value={display} start={index} end={end} first={true} />
        );
      case 'searchAll':
        return <div className="searchAllSuggestion">{suggestion.display}</div>;
      case 'browseAll':
        return (
          <a className="browseEntireLL" href="/resources/">
            Browse the entire Learning Library
            <SvgIcon name="arrowRight" aria-hidden />
          </a>
        );
      default:
        return (
          <Suggestion value={display} start={index} end={end} first={false} />
        );
    }
  };

  function shouldRenderSuggestions(value) {
    if (page === 'search') return true;

    return value.trim().length >= 0;
  }

  function renderSectionTitle(section) {
    if (section.title === 'autosuggest' || section.title === '') {
      return null;
    }
    return <strong>{section.title}</strong>;
  }

  function getSectionSuggestions(section) {
    return section.entries;
  }

  return (
    <div ref={wrapperRef} onClick={() => setIsOpen(true)}>
      <Autosuggest
        suggestions={isOpen ? suggestions : []}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={{
          className: searchInputClass,
          placeholder,
          value,
          onChange,
          name: 'q',
          maxLength: '2000',
        }}
        multiSection={true}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        shouldRenderSuggestions={shouldRenderSuggestions}
      />
    </div>
  );
};

AutoSuggestWrapper.propTypes = {
  doSubmit: PropTypes.func,
  value: PropTypes.string,
  searchInputClass: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  page: PropTypes.string,
  searchMetaTitle: PropTypes.string,
  query: PropTypes.string,
  searchAllExist: PropTypes.bool,
  setSearchWithin: PropTypes.func,
  setSearchAutoSuggest: PropTypes.func,
};

export default AutoSuggestWrapper;
